import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiVisitEngine } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/query-keys'
import type { IApiResponse } from '@/shared/types/api'
import type {
  ICreateDonorDocumentPayload,
  IDonorDocument,
} from '../model/donor-documents.types'

// ── Raw API ───────────────────────────────────────────────────────────────────

export const donorDocumentsApi = {
  getDocuments: async (donorId: string) => {
    const response = await apiVisitEngine.get<IApiResponse<IDonorDocument[]>>(
      `/v1/donor-card/${donorId}/documents`,
    )
    return response.data.data
  },

  createDocument: async (
    donorId: string,
    payload: ICreateDonorDocumentPayload,
  ) => {
    const formData = new FormData()
    formData.append('date', payload.date)
    if (payload.type) formData.append('type', payload.type)
    if (payload.note) formData.append('note', payload.note)
    if (payload.doctor) formData.append('doctor', payload.doctor)
    if (payload.file) formData.append('file', payload.file)

    const response = await apiVisitEngine.post<IApiResponse<IDonorDocument>>(
      `/v1/donor-card/${donorId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        _skipGlobalErrorHandler: true,
      } as any,
    )
    return response.data.data
  },

  /**
   * The file is served inline behind bearer auth, so it is fetched as a blob
   * rather than linked to. `file_url` from the list carries the gateway's `/api`
   * prefix, which visit-engine's base URL already ends in — hence the path is
   * rebuilt from the ids.
   */
  getDocumentFile: async (donorId: string, documentId: string) => {
    const response = await apiVisitEngine.get<Blob>(
      `/v1/donor-card/${donorId}/documents/${documentId}/file`,
      { responseType: 'blob' },
    )
    return response.data
  },
}

// ── Query hooks ───────────────────────────────────────────────────────────────

export const useGetDonorDocumentsQuery = (donorId: string, enabled = true) => {
  const { i18n } = useTranslation()

  return useQuery({
    // Type and status labels are localized server-side via Accept-Language.
    queryKey: [...queryKeys.donorCard.documents(donorId), i18n.language],
    queryFn: () => donorDocumentsApi.getDocuments(donorId),
    enabled: !!donorId && enabled,
    retry: false,
  })
}

// ── Mutation hooks ────────────────────────────────────────────────────────────

/**
 * A new certificate can flip `has_valid_certificate` on the donor card, so the
 * card is refreshed alongside the list.
 */
export const useCreateDonorDocumentMutation = (donorId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ICreateDonorDocumentPayload) =>
      donorDocumentsApi.createDocument(donorId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.donorCard.documents(donorId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.donorCard.detail(donorId),
      })
    },
  })
}

/** Opens the certificate's file in a new tab (PDFs and images render inline). */
export const useOpenDonorDocumentFileMutation = (donorId: string) =>
  useMutation({
    mutationFn: (documentId: string) =>
      donorDocumentsApi.getDocumentFile(donorId, documentId),
    onSuccess: (blob) => {
      const objectUrl = URL.createObjectURL(blob)
      window.open(objectUrl, '_blank')
      // The new tab has loaded the blob by then; release it afterwards.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    },
  })
